import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { apiService } from '@/lib/api';
import { getJobStatusStyle } from '@/lib/jobStatusStyle';
import { ContactCreateDTO, ContactDTO, ContactUpdateDTO } from '@/types';
import { ContactList, ContactListItem } from './contacts/ContactList';
import { ContactFormModal } from './contacts/ContactFormModal';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { JobDetailsModal } from './JobDetailsModal';
import { Search, Plus, Edit, Trash2, Phone, MessageSquare, Star, X, Briefcase, ClipboardList } from 'lucide-react';
import clsx from 'clsx';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { JobContactConversationModal } from '@/components/JobContactConversationModal';
import { CallMethodSheet } from '@/components/CallMethodSheet';
import { CallLauncher, type CallRequest } from '@/components/CallLauncher';
import { Capacitor } from '@capacitor/core';
import { navigateApp, useBackNavigationLayer } from '@/lib/navigation';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { LeadStatus } from '@/types';
import { LEAD_STATUS_CHIP, LEAD_STATUS_LABEL } from '@/lib/leadStatus';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import LinkedEstimates from './LinkedEstimates';
import { NewJobModal } from './NewJobModal';
import { NewEstimateModal } from './NewEstimateModal';

interface ContactsPageProps {
  initialContactId?: string | null;
  onContactSelected?: (contactId: string | null) => void;
}

export function ContactsPage({ initialContactId = null, onContactSelected }: ContactsPageProps) {
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [selectedContactId, setSelectedContactId] = useState<string | null>(initialContactId);
  useEffect(() => {
    setSelectedContactId(initialContactId ?? null);
  }, [initialContactId]);

  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<'create' | 'edit'>('create');
  const [editingContact, setEditingContact] = useState<ContactDTO | null>(null);
  const [contactToDelete, setContactToDelete] = useState<ContactDTO | null>(null);

  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { selectedCompanyId } = useGlobalCompany();
  const handleMobileContactClose = useBackNavigationLayer({
    enabled: isMobile && !!selectedContactId,
    onBack: () => {
      setSelectedContactId(null);
      onContactSelected?.(null);
    },
    state: { contactDetailsModal: true },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const contactsQuery = useQuery<any>({
    queryKey: ['contacts', { searchTerm, page, pageSize, companyId: selectedCompanyId }],
    queryFn: async () => apiService.getContacts({ search: searchTerm, page, pageSize }),
    placeholderData: (previousData: any) => previousData,
    staleTime: 30_000,
  });

  const contactsData = contactsQuery.data as any;
  const contacts: ContactDTO[] = useMemo(() => {
    if (!contactsData) return [];
    if (Array.isArray(contactsData)) return contactsData;
    return contactsData.items ?? contactsData.contacts ?? contactsData.data ?? contactsData.results ?? [];
  }, [contactsData]);

  const totalContacts = contactsData?.total ?? contactsData?.total_count ?? contacts.length;
  const totalPages = Math.max(1, Math.ceil(totalContacts / pageSize));

  const createContactMutation = useMutation({
    mutationFn: (payload: ContactCreateDTO) => apiService.createContact(payload),
    onSuccess: (created: ContactDTO) => {
      addNotification(notificationService.success('Contact created', 'The contact has been added.'));
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setContactModalOpen(false);
      setEditingContact(null);
      setSelectedContactId(created.id);
      onContactSelected?.(created.id);
    },
    onError: (error: any) => {
      addNotification(notificationService.error('Failed to create contact', error.message ?? 'Please try again.'));
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ContactUpdateDTO }) =>
      apiService.updateContact(id, payload),
    onSuccess: (updated: ContactDTO) => {
      addNotification(notificationService.success('Contact updated', 'The contact has been updated.'));
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', updated.id] });
      setContactModalOpen(false);
      setEditingContact(null);
    },
    onError: (error: any) => {
      addNotification(notificationService.error('Failed to update contact', error.message ?? 'Please try again.'));
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteContact(id),
    onSuccess: () => {
      addNotification(notificationService.success('Contact deleted', 'The contact has been removed.'));
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setDeleteConfirmOpen(false);
      if (selectedContactId === contactToDelete?.id) {
        setSelectedContactId(null);
        onContactSelected?.(null);
      }
      setContactToDelete(null);
    },
    onError: (error: any) => {
      addNotification(notificationService.error('Failed to delete contact', error.message ?? 'Please try again.'));
    },
  });

  const openCreateModal = () => {
    setContactModalMode('create');
    setEditingContact(null);
    setContactModalOpen(true);
  };

  const openEditModal = (contact: ContactDTO) => {
    setContactModalMode('edit');
    setEditingContact(contact);
    setContactModalOpen(true);
  };

  const handleContactSubmit = async ({ contact }: { contact: ContactCreateDTO | ContactUpdateDTO }) => {
    if (contactModalMode === 'edit' && editingContact) {
      await updateContactMutation.mutateAsync({ id: editingContact.id, payload: contact as ContactUpdateDTO });
    } else {
      const createPayload: ContactCreateDTO = {
        name: contact.name ?? '',
        phone: contact.phone ?? '',
        additional_phone: contact.additional_phone,
        email: contact.email,
        notes: contact.notes,
      };
      await createContactMutation.mutateAsync(createPayload);
    }
  };

  const handleRemoveContact = (contact: ContactDTO) => {
    setContactToDelete(contact);
    setDeleteConfirmOpen(true);
  };

  const contactItems: ContactListItem[] = contacts.map((contact) => ({
    id: contact.id,
    contact,
    notes: contact.notes,
  }));

  return (
    <div className="space-y-4">
      <div className={`grid grid-cols-1 ${!isMobile ? 'xl:grid-cols-[1.3fr_1fr]' : ''} gap-6`}>
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-3 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-10 flex-1 items-center gap-2 rounded-lg border border-gray-300 px-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contacts by name, phone, or email"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="w-3 flex-1 text-sm text-gray-700 focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex h-10 w-10 items-center justify-center gap-1 rounded-lg bg-blue-600 text-xs font-medium text-white shadow-sm transition-colors hover:bg-blue-700 flex-shrink-0 sm:w-auto sm:px-3"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Contact</span>
              </button>
            </div>
          </div>

          <div className="p-4">
            {contactsQuery.isLoading ? (
              <div className="py-12 text-center text-gray-500">
                <LoadingSpinner />
                <p className="mt-3 text-sm">Loading contacts...</p>
              </div>
            ) : contactItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                No contacts match your search. Try adjusting your filters or add a new contact.
              </div>
            ) : (
              <ContactList
                contacts={contactItems}
                selectable={true}
                onSelect={(item) => {
                  setSelectedContactId(item.id);
                  onContactSelected?.(item.id);
                }}
                onEdit={(item) => {
                  const contact = contacts.find((entry) => entry.id === item.id);
                  if (contact) {
                    openEditModal(contact);
                  }
                }}
                onRemove={(item) => {
                  const contact = contacts.find((entry) => entry.id === item.id);
                  if (contact) {
                    handleRemoveContact(contact);
                  }
                }}
              />
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
            <span>
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalContacts)} of {totalContacts}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className={clsx(
                  'rounded-lg border border-gray-200 px-3 py-1.5 font-medium transition-colors hover:bg-gray-50',
                  page === 1 && 'cursor-not-allowed opacity-50'
                )}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className={clsx(
                  'rounded-lg border border-gray-200 px-3 py-1.5 font-medium transition-colors hover:bg-gray-50',
                  page === totalPages && 'cursor-not-allowed opacity-50'
                )}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Desktop: sticky side panel */}
        {!isMobile && (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm min-h-[600px] sticky top-4 self-start">
            <ContactDetailsPanel
              contactId={selectedContactId}
              onEdit={(contact) => openEditModal(contact)}
              onDelete={(contact) => handleRemoveContact(contact)}
            />
          </div>
        )}
      </div>

      {/* Mobile: modal overlay */}
      {isMobile && selectedContactId && createPortal(
        <div className="fixed inset-0 z-[60] flex flex-col bg-white safe-area-top safe-area-bottom">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-semibold text-gray-900">Contact Details</h2>
            <button
              type="button"
              onClick={handleMobileContactClose}
              className="p-2 -mr-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Modal Content */}
          <div className="flex-1 overflow-auto">
            <ContactDetailsPanel
              contactId={selectedContactId}
              onEdit={(contact) => openEditModal(contact)}
              onDelete={(contact) => handleRemoveContact(contact)}
            />
          </div>
        </div>,
        document.body
      )}

      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setContactModalOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleContactSubmit}
        contact={editingContact ?? undefined}
        title={contactModalMode === 'edit' ? 'Edit Contact' : 'New Contact'}
        confirmLabel={contactModalMode === 'edit' ? 'Save Changes' : 'Create Contact'}
        isSubmitting={createContactMutation.isPending || updateContactMutation.isPending}
      />

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete Contact"
        cancelText="Cancel"
        isLoading={deleteContactMutation.isPending}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setContactToDelete(null);
        }}
        onConfirm={async () => {
          if (contactToDelete) {
            await deleteContactMutation.mutateAsync(contactToDelete.id);
          }
        }}
      />
    </div>
  );
}

interface ContactDetailsPanelProps {
  contactId: string | null;
  /**
   * Opened from this job: loads the contact through the job, so the server applies the job's
   * protections (technicians see only their own past jobs, and masked phones stay masked).
   */
  jobId?: string;
  /** Edit / Delete buttons only render when these are passed. */
  onEdit?: (contact: ContactDTO) => void;
  onDelete?: (contact: ContactDTO) => void;
  /** Runs before opening an estimate or a lead — lets a host overlay (e.g. a job) get out of the way. */
  onBeforeNavigate?: () => void;
}

export function ContactDetailsPanel({ contactId, jobId, onEdit, onDelete, onBeforeNavigate }: ContactDetailsPanelProps) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isNewJobOpen, setIsNewJobOpen] = useState(false);
  const [isNewEstimateOpen, setIsNewEstimateOpen] = useState(false);
  const [conversationModalState, setConversationModalState] = useState<{
    isOpen: boolean;
    contactId: string;
    contactName: string;
  }>({ isOpen: false, contactId: '', contactName: '' });

  const [callSheetState, setCallSheetState] = useState<{
    isOpen: boolean;
    toE164: string;
    displayName: string;
  }>({ isOpen: false, toE164: '', displayName: '' });
  const [callRequest, setCallRequest] = useState<CallRequest | null>(null);
  const { isFeatureAllowed } = usePermissions();
  const isTechnician = useCurrentUser().userRole === 'technician';
  const canViewLeads = isFeatureAllowed('leads') && !isTechnician;
  // The estimates search already limits technicians to estimates they created or are assigned to.
  const canViewEstimates = isFeatureAllowed('estimates');
  const canCreateJobs = isFeatureAllowed('jobs.create');
  const canCreateEstimates = isFeatureAllowed('estimates.create');
  const { openEstimate } = useVoiceCall();

  // On desktop the softphone is the only sensible way to call ("Regular Phone Call" opens a phone
  // dialer the machine doesn't have), so skip the method sheet and dial straight away — matching the
  // calls and job-details pages. Mobile keeps the sheet: native genuinely offers both, and a mobile
  // browser hands off to tel: without showing it anyway.
  const callsDirectly = !Capacitor.isNativePlatform() && !isMobile;

  const startCall = (toE164: string, displayName: string) => {
    if (callsDirectly) setCallRequest({ toE164, displayName: displayName || undefined });
    else setCallSheetState({ isOpen: true, toE164, displayName });
  };

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsJobModalOpen(true);
  };
  const contactQuery = useQuery({
    queryKey: jobId ? ['job-contact', jobId, contactId] : ['contact', contactId],
    queryFn: () => (jobId ? apiService.getJobContact(jobId, contactId!) : apiService.getContactById(contactId!)),
    enabled: !!contactId,
    staleTime: 60_000,
  });
  const contactLeadsQuery = useQuery({
    queryKey: ['contact-leads', contactId],
    queryFn: () => apiService.searchLeads({
      contact_id: contactId!,
      statuses: ['New', 'FollowUp', 'NoAnswer', 'Accepted', 'Declined'] as LeadStatus[],
      archive_mode: 'All',
      sort_by: 'received_at',
      sort_direction: 'desc',
      page: 1,
      page_size: 100,
    }),
    enabled: !!contactId && canViewLeads,
    staleTime: 30_000,
  });

  if (!contactId) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-gray-500">
        <Phone className="h-10 w-10 text-gray-300" />
        <p>Select a contact to view details.</p>
      </div>
    );
  }

  if (contactQuery.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (contactQuery.isError) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-gray-500">
        <p>Unable to load contact details.</p>
        {contactQuery.error && (
          <p className="text-xs text-red-500">Error: {String(contactQuery.error)}</p>
        )}
      </div>
    );
  }

  if (!contactQuery.data) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-gray-500">
        <LoadingSpinner />
        <p>Loading contact details...</p>
      </div>
    );
  }

  const contact = contactQuery.data as ContactDTO;
  const jobs = contact.jobs ?? [];

  const formatJobDate = (raw?: string | null) => {
    if (!raw) return 'Date not set';

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return 'Date not set';
    }

    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatJobTotal = (total?: number) => {
    const amount = typeof total === 'number' ? total : 0;
    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
    });
  };

  // Phone: the header buttons share one row as small tiles (icon over label) under the name.
  const headerButtonClass = clsx(
    'rounded-lg border font-medium transition-colors',
    isMobile ? 'flex flex-1 flex-col items-center gap-1 px-1 py-2 text-[11px]' : 'flex items-center gap-1 px-3 py-1.5 text-xs'
  );
  const headerIconClass = isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-gray-200 px-4 py-4">
        <div className={clsx('flex gap-4', isMobile ? 'flex-col gap-3' : 'items-start justify-between')}>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{contact.name}</h2>
            <p className="text-sm text-gray-500">Contact overview and recent jobs</p>
          </div>
          <div className={clsx('flex items-center gap-2', isMobile ? 'w-full' : 'flex-wrap justify-end')}>
            {canCreateJobs && (
              <button
                type="button"
                onClick={() => setIsNewJobOpen(true)}
                className={clsx(headerButtonClass, 'border-blue-200 text-blue-600 hover:bg-blue-50')}
              >
                <Briefcase className={headerIconClass} />
                New Job
              </button>
            )}
            {canCreateEstimates && (
              <button
                type="button"
                onClick={() => setIsNewEstimateOpen(true)}
                className={clsx(headerButtonClass, 'border-amber-200 text-amber-700 hover:bg-amber-50')}
              >
                <ClipboardList className={headerIconClass} />
                New Estimate
              </button>
            )}
            {onEdit && <button
              type="button"
              onClick={() => onEdit(contact)}
              className={clsx(headerButtonClass, 'border-gray-200 text-gray-600 hover:bg-gray-50')}
            >
              <Edit className={headerIconClass} />
              Edit
            </button>}
            {onDelete && <button
              type="button"
              onClick={() => onDelete(contact)}
              className={clsx(headerButtonClass, 'border-red-200 text-red-600 hover:bg-red-50')}
            >
              <Trash2 className={headerIconClass} />
              Delete
            </button>}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 min-h-0">
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Star className="h-4 w-4 text-yellow-500" />
              Contact Information
            </h3>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-gray-700">
              <div>
                <span className="font-medium text-gray-600">Phone:</span>{' '}
                <span>{contact.phone ? (contact.phone_masked ? contact.phone : formatPhoneNumber(contact.phone)) : 'Not provided'}</span>
              </div>
              {contact.additional_phone && (
                <div>
                  <span className="font-medium text-gray-600">Additional Phone:</span>{' '}
                  <span>{contact.phone_masked ? contact.additional_phone : formatPhoneNumber(contact.additional_phone)}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-600">Email:</span>{' '}
                <span>{contact.email || 'Not provided'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Notes:</span>{' '}
                <span>{contact.notes || 'No additional notes'}</span>
              </div>
            </div>
            {/* Masked (technician on a masked job): there's no real number to dial or text here — the
                job's own contact row places masked calls/SMS through the company number. */}
            {!contact.phone_masked && <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {/* Call button on both mobile and desktop; startCall picks the direct softphone dial
                  (desktop) or the method sheet (mobile/native). */}
              <button
                type="button"
                disabled={!contact.phone}
                onClick={() => {
                  if (contact.phone) startCall(contact.phone, contact.name || '');
                }}
                className="flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1 font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Phone className="h-3.5 w-3.5" />
                Call
              </button>
              <button
                className="flex items-center gap-1 rounded-lg border border-green-200 px-3 py-1 font-medium text-green-600 transition-colors hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!contact.phone}
                onClick={() => {
                  if (contact.phone) {
                    setConversationModalState({
                      isOpen: true,
                      contactId: contact.id,
                      contactName: contact.name || 'Contact',
                    });
                  }
                }}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                SMS
              </button>
            </div>}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">Recent Jobs</h3>
            {jobs.length === 0 ? (
              <p className="py-6 text-sm text-gray-500">No jobs associated with this contact yet.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {jobs.map((job) => (
                  <div
                    key={job.job_id}
                    className="rounded-lg border border-gray-200 p-3 cursor-pointer transition-colors hover:bg-gray-50 hover:border-blue-300"
                    onClick={() => handleJobClick(job.job_id)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {job.job_type_name || 'Job'}
                          {job.invoice_number ? ` #${job.invoice_number}` : ''}
                        </p>
                        <p className="text-xs text-gray-500">{formatJobDate(job.job_date)}</p>
                      </div>
                      <div className="text-right">
                        {job.canceled_at ? (
                          <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
                            Canceled
                          </span>
                        ) : (
                          <span
                            className="rounded-full px-2 py-0.5 text-xs font-medium"
                            style={getJobStatusStyle(job.status_color)}
                          >
                            {job.status || 'Status unknown'}
                          </span>
                        )}
                        <p className="mt-2 text-sm font-semibold text-gray-900">{formatJobTotal(job.total)}</p>
                      </div>
                    </div>
                    {job.address && (
                      <p className="mt-2 text-xs text-gray-500">Location: {job.address}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {canViewLeads && <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">Lead History</h3>
            {contactLeadsQuery.isLoading ? <p className="py-4 text-sm text-gray-500">Loading leads…</p> : (contactLeadsQuery.data?.leads ?? []).length === 0 ? <p className="py-4 text-sm text-gray-500">No leads associated with this contact yet.</p> : <div className="mt-3 space-y-2">{contactLeadsQuery.data!.leads.map(lead => <button key={lead.id} className="flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:border-blue-300 hover:bg-gray-50" onClick={() => { onBeforeNavigate?.(); navigateApp(`/leads/${lead.id}`); }}><div><p className="text-sm font-medium text-gray-900">{lead.job_type?.name || lead.description || 'Lead'}</p><p className="text-xs text-gray-500">{formatInstantInCompanyTimeZone(lead.received_at, lead.company_timezone || undefined)}{lead.address ? ` · ${lead.address}` : ''}</p></div><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEAD_STATUS_CHIP[lead.status]}`}>{LEAD_STATUS_LABEL[lead.status]}</span></button>)}</div>}
          </div>}
          {canViewEstimates && <LinkedEstimates
            filter={{ contact_id: contact.id }}
            companyId={contact.company_id}
            onOpen={(estimateId) => { onBeforeNavigate?.(); openEstimate(estimateId); }}
            emptyText="No estimates associated with this contact yet."
          />}
        </div>
      </div>

      {/* Portaled to body: the desktop job modal doesn't portal itself, and inline it's trapped in the
          sticky side panel's stacking context, so the app's top bar painted over its header. */}
      {createPortal(
        <JobDetailsModal
          isOpen={isJobModalOpen}
          onClose={() => {
            setIsJobModalOpen(false);
            setSelectedJobId(null);
          }}
          jobId={selectedJobId}
        />,
        document.body,
      )}

      <NewJobModal
        isOpen={isNewJobOpen}
        onClose={() => setIsNewJobOpen(false)}
        onJobCreated={() => {
          setIsNewJobOpen(false);
          queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
          queryClient.invalidateQueries({ queryKey: ['job-contact'] });
          queryClient.invalidateQueries({ queryKey: ['jobs'] });
        }}
        initialValues={{ company_id: contact.company_id, contacts: [contact] }}
      />

      <NewEstimateModal
        isOpen={isNewEstimateOpen}
        onClose={() => setIsNewEstimateOpen(false)}
        onEstimateCreated={() => {
          setIsNewEstimateOpen(false);
          queryClient.invalidateQueries({ queryKey: ['contact-estimates', contact.id] });
        }}
        initialContact={contact}
      />

      {/* Contact conversation — no job context; falls back to the Send SMS modal when no thread exists */}
      <JobContactConversationModal
        isOpen={conversationModalState.isOpen}
        onClose={() => setConversationModalState({ ...conversationModalState, isOpen: false })}
        contactId={conversationModalState.contactId}
        which="primary"
        masked={false}
        contactName={conversationModalState.contactName}
      />

      {/* Call Method Sheet (mobile/native) */}
      <CallMethodSheet
        isOpen={callSheetState.isOpen}
        onClose={() => setCallSheetState({ ...callSheetState, isOpen: false })}
        toE164={callSheetState.toE164}
        displayName={callSheetState.displayName}
      />

      {/* Direct dial (desktop) — only prompts when several caller-ID numbers are usable */}
      <CallLauncher request={callRequest} onDone={() => setCallRequest(null)} />
    </div>
  );
}

